import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Clock } from "lucide-react";
import { SEO } from "../components/SEO";
import { MaskedLines, FadeUp, Overline, GoldRule } from "../components/Reveal";
import { SERVICES } from "../data/services";

const SITE_URL = "https://lunarambitions.com";

const ServicePage = ({ service }) => {
  const nameParts = service.name.split(" | ");
  const others = SERVICES.filter((s) => s.slug !== service.slug);
  const pageUrl = `${SITE_URL}${service.slug}`;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.schemaName,
    description: service.seoDescription,
    url: pageUrl,
    provider: {
      "@id": "https://lunarambitions.com/#business",
    },
    areaServed: "Orlando, FL",
    offers: {
      "@type": "Offer",
      price: service.price.replace("$", ""),
      priceCurrency: "USD",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: `${SITE_URL}/services`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: service.schemaName,
        item: pageUrl,
      },
    ],
  };

  return (
    <div data-testid={`service-page-${service.slug.slice(1)}`}>
      <SEO
        title={service.seoTitle}
        description={service.seoDescription}
        extraSchema={[serviceSchema, breadcrumbSchema]}
      />

      <section className="relative overflow-hidden bg-white pt-52 pb-16 lg:pt-72 sm:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp delay={0}>
            <Link
              to="/services"
              data-testid="service-back-link"
              className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium text-navy/50 hover:text-gold transition-colors duration-300"
            >
              <ArrowLeft
                size={14}
                className="transition-transform duration-300 group-hover:-translate-x-1"
              />
              Service Options
            </Link>
          </FadeUp>

          <div className="mt-10 grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 }}
              >
                <Overline>Lunar Ambitions · Orlando, FL</Overline>
              </motion.div>
              <h1
                className="mt-6 font-display font-normal tracking-tight leading-[1.06] text-navy text-4xl sm:text-5xl lg:text-6xl"
                data-testid="service-heading"
              >
                <MaskedLines
                  lines={nameParts}
                  lastLineClassName={
                    nameParts.length > 1 ? "italic text-gold" : ""
                  }
                />
              </h1>
              <FadeUp delay={0.5}>
                <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4">
                  <span
                    className="font-display text-3xl text-navy"
                    data-testid="service-price"
                  >
                    {service.price}
                  </span>
                  <span
                    className="inline-flex items-center gap-2 text-sm text-navy/60 tracking-wide"
                    data-testid="service-duration"
                  >
                    <Clock size={15} className="text-gold" />
                    {service.durationShort}
                  </span>
                </div>
              </FadeUp>
              <FadeUp delay={0.6}>
                <a
                  href={service.bookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-book-button"
                  className="group mt-10 inline-flex items-center gap-3 bg-navy text-white text-sm tracking-[0.16em] uppercase font-medium px-9 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
                >
                  {service.bookLabel}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </FadeUp>
            </div>

            <div className="lg:col-span-4">
              <FadeUp delay={0.35}>
                <Overline as="h2" data-testid="service-who-its-for-heading">
                  Who It's For
                </Overline>
                <div className="mt-4 border-l-2 border-gold pl-6 py-1">
                  <p
                    className="font-display italic text-lg sm:text-xl text-navy/80 leading-relaxed"
                    data-testid="service-ideal-for"
                  >
                    {service.idealFor}
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pb-10 sm:pb-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="relative lg:col-span-5">
            <FadeUp>
              <div className="relative max-w-sm mx-auto lg:mx-0">
                <div
                  aria-hidden="true"
                  className="absolute -inset-3 border border-gold/40 translate-x-4 translate-y-4"
                />
                <div className="relative overflow-hidden shadow-[0_40px_80px_-40px_rgba(4,46,100,0.4)]">
                  <img
                    src={service.image}
                    alt={service.imageAlt}
                    className="w-full h-auto object-contain"
                    data-testid="service-image"
                  />
                </div>
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <Overline as="h2" data-testid="service-what-to-expect-heading">
                What to Expect
              </Overline>
              <GoldRule className="w-16 mt-4" />
              <p
                className="mt-8 text-base sm:text-lg text-navy/75 leading-relaxed font-light"
                data-testid="service-description"
              >
                {service.description}
              </p>
              <p className="mt-6 text-sm font-semibold text-navy" data-testid="service-duration-note">
                {service.duration}
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="pt-10 sm:pt-14 pb-20 sm:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <Overline>Service Options</Overline>
              <Link
                to="/services"
                data-testid="service-all-services-link"
                className="text-xs tracking-[0.2em] uppercase font-medium text-navy/60 hover:text-gold transition-colors duration-300"
              >
                View All
              </Link>
            </div>
          </FadeUp>
          <ul className="mt-10 divide-y divide-navy/10 border-y border-navy/10">
            {others.slice(0, 5).map((s) => (
              <li key={s.slug}>
                <Link
                  to={s.slug}
                  data-testid={`service-related-${s.slug.slice(1)}`}
                  className="group flex items-center justify-between gap-6 py-5 transition-colors duration-300 hover:bg-[#F9F8F6] px-2 sm:px-4 -mx-2 sm:-mx-4"
                >
                  <span className="font-display text-xl sm:text-2xl text-navy transition-colors duration-300 group-hover:text-gold">
                    {s.name}
                  </span>
                  <span className="flex items-center gap-6 shrink-0">
                    <span className="hidden sm:block text-xs tracking-[0.14em] uppercase text-navy/50">
                      {s.durationShort} · {s.price}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-navy/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
